import React from "react";
import PropTypes from "prop-types";
import { graphql } from "gatsby";

import LinkWrapper from "@/components/linkWrapper";
import MediaWrapper from "@/components/mediaWrapper";
import MarkdownWrapper from "@/components/markdownWrapper";

export const query = graphql`
  fragment HeroWithAppScreenshotData on StrapiComponentHeroWithAppScreenshot {
    title
    topic
    description
    image {
      ...StrapiMediaSubquery
    }
    referenceText
    referenceLogos {
      ...StrapiSharedLink
    }
  }
`;

function WithAppScreenshot({ data }) {
  if (!data) {
    return <template />;
  }
  return (
    <div className="bg-background-off-white">
      <div className="relative overflow-hidden">
        <div className="absolute inset-y-0 h-full w-full" aria-hidden="true">
          <div className="relative h-full">
            <svg
              className="absolute right-full translate-x-1/4 translate-y-1/3 transform sm:translate-x-1/2 md:translate-y-1/2 lg:translate-x-full"
              width={404}
              height={784}
              fill="none"
              viewBox="0 0 404 784"
            >
              <defs>
                <pattern
                  id="e229dbec-10e9-49ee-8ec3-0286ca089edf"
                  x={0}
                  y={0}
                  width={20}
                  height={20}
                  patternUnits="userSpaceOnUse"
                >
                  <rect
                    x={0}
                    y={0}
                    width={4}
                    height={4}
                    className="text-text-light-gray"
                    fill="currentColor"
                  />
                </pattern>
              </defs>
              <rect
                width={404}
                height={784}
                fill="url(#e229dbec-10e9-49ee-8ec3-0286ca089edf)"
              />
            </svg>
            <svg
              className="absolute left-full -translate-x-1/4 -translate-y-3/4 transform sm:-translate-x-1/2 md:-translate-y-1/2 lg:-translate-x-3/4"
              width={404}
              height={784}
              fill="none"
              viewBox="0 0 404 784"
            >
              <defs>
                <pattern
                  id="d2a68204-c383-44b1-b99f-42ccff4e5365"
                  x={0}
                  y={0}
                  width={20}
                  height={20}
                  patternUnits="userSpaceOnUse"
                >
                  <rect
                    x={0}
                    y={0}
                    width={4}
                    height={4}
                    className="text-text-light-gray"
                    fill="currentColor"
                  />
                </pattern>
              </defs>
              <rect
                width={404}
                height={784}
                fill="url(#d2a68204-c383-44b1-b99f-42ccff4e5365)"
              />
            </svg>
          </div>
        </div>

        <div className="relative pb-16 pt-6 sm:pb-24">
          <div className="mx-auto mt-16 max-w-7xl px-4 sm:mt-24 sm:px-6">
            <div className="text-center">
              {data.topic && (
                <h3 className="text-2xl tracking-tight text-text-primary sm:text-3xl">
                  <span className="block">
                    <MarkdownWrapper text={data.topic} />
                  </span>
                </h3>
              )}
              {data.title && (
                <h1 className="text-4xl font-extrabold tracking-tight text-text-primary sm:text-5xl md:text-6xl">
                  <span className="block">
                    <MarkdownWrapper text={data.title} />
                  </span>
                </h1>
              )}
              {data.description && (
                <MarkdownWrapper
                  text={data.description}
                  markdownClassName="mt-3 max-w-md mx-auto text-base text-text-secondary sm:text-lg md:mt-5 md:text-xl md:max-w-3xl"
                  replaceParagraph={false}
                />
              )}
            </div>
          </div>
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex flex-col" aria-hidden="true">
            <div className="flex-1" />
            <div className="w-full flex-1 bg-background-primary-dark" />
          </div>
          {data.image && (
            <div className="mx-auto max-w-7xl px-4 sm:px-6">
              <MediaWrapper
                media={data.image}
                divClassName="relative"
                className="rounded-lg shadow-lg"
              />
            </div>
          )}
        </div>
      </div>
      <div className="bg-background-primary-dark">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
          {data.referenceText && (
            <h2 className="text-center text-sm font-semibold uppercase tracking-wide text-text-secondary">
              <MarkdownWrapper text={data.referenceText} />
            </h2>
          )}
          {Boolean(data.referenceLogos?.length) && (
            <div className="mt-8 grid grid-cols-2 gap-8 md:grid-cols-6 lg:grid-cols-5">
              {data.referenceLogos.map(
                (logo, index) =>
                  (logo.overwriteIcon || logo.link?.icon) && (
                    <div
                      key={index}
                      className="col-span-1 flex justify-center md:col-span-2 lg:col-span-1"
                    >
                      <LinkWrapper
                        to={logo?.link?.url}
                        className={`px-4 py-2 ${
                          logo?.link?.url
                            ? "rounded-md hover:bg-background-light-gray"
                            : ""
                        }`}
                      >
                        <MediaWrapper
                          media={logo.overwriteIcon || logo.link.icon}
                          divClassName="h-12"
                          objectFit="contain"
                        />
                      </LinkWrapper>
                    </div>
                  )
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

WithAppScreenshot.propTypes = {
  data: PropTypes.object,
};

export default WithAppScreenshot;
